/**
 * Creates the component's shadow DOM. Geometry and lifecycle stay outside this
 * factory; it only translates an already resolved presentation state into DOM.
 * @param {object} options Render options.
 * @param {boolean} options.isBottom Whether bottom presentation is active.
 * @param {boolean} options.isOpen Current open state.
 * @param {'left'|'right'|'bottom'} options.direction Configured direction.
 * @param {string} options.maxWidth Base panel width.
 * @param {string} options.maxHeight Panel height limit.
 * @param {boolean} options.hasOpacity Whether panel opacity is animated.
 * @param {HTMLElement} options.closeButton Generated close button.
 * @param {HTMLElement|null} options.sheetHandle Optional resize handle.
 * @param {boolean} options.showBackdrop Whether the backdrop source is enabled.
 * @returns {{fragment: DocumentFragment, elements: object}} Rendered structure and references.
 */
export function createSlidingContainerView({
    isBottom,
    isOpen,
    direction,
    maxWidth,
    maxHeight,
    hasOpacity,
    closeButton,
    sheetHandle,
    showBackdrop,
}) {
    const fragment = document.createDocumentFragment();
    const backdrop = document.createElement('div');
    backdrop.setAttribute('part', 'backdrop');
    backdrop.setAttribute('aria-hidden', 'true');
    backdrop.classList.add('sliding-container-backdrop');
    if (isOpen && showBackdrop) {
        backdrop.style.opacity = 'var(--wje-sliding-container-backdrop-opacity, 1)';
    }

    const wrapper = document.createElement('div');
    wrapper.classList.add('sliding-container-wrapper');

    const spacer = document.createElement('div');
    spacer.classList.add('sliding-container-transparent');
    spacer.style.width = isOpen && !isBottom ? maxWidth : '0px';

    const panels = document.createElement('div');
    panels.classList.add('sliding-container-panels');
    panels.style.width = isBottom ? '0px' : maxWidth;
    if (direction === 'right') {
        panels.style.right = '0px';
    } else {
        panels.style.left = '0px';
    }

    const base = document.createElement('div');
    base.setAttribute('part', 'sliding-container');
    base.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    base.classList.add('native-sliding-container');
    if (!isOpen) base.setAttribute('inert', '');

    base.style.width = isBottom ? '100%' : '0px';
    if (hasOpacity) base.style.opacity = isOpen ? '1' : '0';

    if (isOpen) {
        base.style.width = isBottom ? '100%' : maxWidth;
        if (isBottom) base.style.transform = 'translateY(0)';
    } else if (isBottom) {
        base.style.transform = 'translateY(100%)';
    }

    if (isBottom) {
        Object.assign(base.style, {
            left: '0px',
            right: '0px',
            bottom: '0px',
            maxHeight,
        });
    } else if (direction === 'right') {
        base.style.right = '0px';
    } else {
        base.style.left = '0px';
    }

    const baseInner = document.createElement('div');
    baseInner.classList.add('native-sliding-container-inner');
    baseInner.style.width = isBottom ? '100%' : maxWidth;
    if (isBottom) baseInner.style.maxHeight = maxHeight;

    const defaultSlot = document.createElement('slot');
    baseInner.append(defaultSlot, closeButton);
    if (sheetHandle) base.append(sheetHandle);
    base.append(baseInner);

    const expanded = document.createElement('div');
    expanded.setAttribute('part', 'expanded');
    expanded.setAttribute('aria-hidden', 'true');
    expanded.setAttribute('inert', '');
    expanded.classList.add('expanded-sliding-container');
    Object.assign(expanded.style, {
        visibility: 'hidden',
        width: '0px',
    });

    const expandedInner = document.createElement('div');
    expandedInner.classList.add('expanded-sliding-container-inner');
    const expandedSlot = document.createElement('slot');
    expandedSlot.name = 'expanded';
    expandedInner.append(expandedSlot);
    expanded.append(expandedInner);

    panels.append(base, expanded);
    wrapper.append(spacer, panels);
    fragment.append(backdrop, wrapper);

    return {
        fragment,
        elements: {
            backdropElement: backdrop,
            wrapperDiv: wrapper,
            transparentDiv: spacer,
            panelsAnchor: panels,
            nativeElement: base,
            nativeInner: baseInner,
            expandedElement: expanded,
            expandedInner,
            expandedSlotElement: expandedSlot,
        },
    };
}
